/*
 * <delivery-estimate> — fills in "Thu, Oct 15 – Mon, Oct 19" style arrival dates.
 *
 * Mirrors how Shopify calculates manual delivery dates at checkout:
 * - Orders placed before the cutoff hour (shipping origin time zone) on a
 *   business day count that day; later orders start the next business day.
 * - Fulfillment time, then transit time, are added in business days.
 * - Business days are Monday–Friday, skipping US federal holidays.
 *
 * All date math uses UTC Date objects that represent calendar days in the
 * store's time zone, so the visitor's own time zone never shifts the result.
 */
(function() {
  if (!window.customElements || customElements.get('delivery-estimate')) {
    return;
  }

  var DAY_MS = 24 * 60 * 60 * 1000;
  var holidayCache = {};

  function utcDate(year, monthIndex, day) {
    return new Date(Date.UTC(year, monthIndex, day));
  }

  function addDays(date, days) {
    return new Date(date.getTime() + days * DAY_MS);
  }

  function dayKey(date) {
    return date.toISOString().slice(0, 10);
  }

  // nth weekday of a month (n = -1 for the last one). weekday: 0 = Sunday.
  function nthWeekday(year, monthIndex, weekday, n) {
    if (n > 0) {
      var first = utcDate(year, monthIndex, 1);
      var offset = (weekday - first.getUTCDay() + 7) % 7;
      return utcDate(year, monthIndex, 1 + offset + (n - 1) * 7);
    }
    var last = utcDate(year, monthIndex + 1, 0);
    return addDays(last, -((last.getUTCDay() - weekday + 7) % 7));
  }

  // Fixed-date holidays that land on a weekend are observed Friday/Monday.
  function observed(date) {
    var weekday = date.getUTCDay();
    if (weekday === 6) return addDays(date, -1);
    if (weekday === 0) return addDays(date, 1);
    return date;
  }

  function federalHolidays(year) {
    if (holidayCache[year]) {
      return holidayCache[year];
    }

    var days = [
      observed(utcDate(year, 0, 1)),   // New Year's Day
      nthWeekday(year, 0, 1, 3),       // Martin Luther King Jr. Day
      nthWeekday(year, 1, 1, 3),       // Presidents' Day
      nthWeekday(year, 4, 1, -1),      // Memorial Day
      observed(utcDate(year, 5, 19)),  // Juneteenth
      observed(utcDate(year, 6, 4)),   // Independence Day
      nthWeekday(year, 8, 1, 1),       // Labor Day
      nthWeekday(year, 9, 1, 2),       // Columbus Day
      observed(utcDate(year, 10, 11)), // Veterans Day
      nthWeekday(year, 10, 4, 4),      // Thanksgiving
      observed(utcDate(year, 11, 25))  // Christmas Day
    ];

    var set = {};
    days.forEach(function(date) {
      set[dayKey(date)] = true;
    });
    holidayCache[year] = set;
    return set;
  }

  function isBusinessDay(date) {
    var weekday = date.getUTCDay();
    if (weekday === 0 || weekday === 6) {
      return false;
    }
    return !federalHolidays(date.getUTCFullYear())[dayKey(date)];
  }

  function nextBusinessDay(date) {
    var next = addDays(date, 1);
    while (!isBusinessDay(next)) {
      next = addDays(next, 1);
    }
    return next;
  }

  function addBusinessDays(date, count) {
    var result = date;
    for (var i = 0; i < count; i++) {
      result = nextBusinessDay(result);
    }
    return result;
  }

  // Current calendar day and time in the store's time zone.
  function nowInTimeZone(timeZone) {
    var parts = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23'
    }).formatToParts(new Date()).forEach(function(part) {
      parts[part.type] = parseInt(part.value, 10);
    });

    return {
      date: utcDate(parts.year, parts.month - 1, parts.day),
      minutes: parts.hour * 60 + parts.minute
    };
  }

  var dateFormat = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });

  function formatDate(date) {
    return dateFormat.format(date);
  }

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.render();
      this.timer = setInterval(this.render.bind(this), 60 * 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    render() {
      var timeZone = this.dataset.timeZone || 'America/Los_Angeles';
      var cutoffMinutes = parseInt(this.dataset.cutoffHour || '12', 10) * 60;
      var fulfillmentDays = parseInt(this.dataset.fulfillmentDays || '0', 10);

      var now = nowInTimeZone(timeZone);
      var beforeCutoff = isBusinessDay(now.date) && now.minutes < cutoffMinutes;
      var firstDay = beforeCutoff ? now.date : nextBusinessDay(now.date);
      var shipDate = addBusinessDays(firstDay, fulfillmentDays);

      this.querySelectorAll('[data-arrival]').forEach(function(el) {
        var min = addBusinessDays(shipDate, parseInt(el.dataset.min, 10));
        var max = addBusinessDays(shipDate, parseInt(el.dataset.max, 10));
        el.textContent = dayKey(min) === dayKey(max)
          ? formatDate(min)
          : formatDate(min) + ' – ' + formatDate(max);
      });

      this.hidden = false;
    }
  }

  customElements.define('delivery-estimate', DeliveryEstimate);
})();
